"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { cn } from "@/lib/utils";
import { PAGE_SURFACE_CLASS } from "@/components/ui/variants";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { markRouteInteractiveReady } from "@/lib/analytics/route-interactive";
import { clampDurationMs } from "@/lib/analytics/duration";
import { startForegroundTimer } from "@/lib/analytics/foreground-timer";

/**
 * BRE-7919: the logo is an inline SVG pulsed with CSS, not a Lottie player.
 * Every route's loading.tsx renders this, so it is first-paint JS on every
 * page: the player cost ~318 KB min / 83 KB gz to draw a 6.7 KB animation.
 *
 * BRE-6110: `children` is destructured explicitly rather than left in `...rest`.
 * It used to ride the spread onto the `div`, where JSX children always win over
 * spread children — so callers that passed a message got a bare logo and the
 * message was silently dropped. RxDBSyncProvider's "Reconnecting... (attempt
 * N/3)" had never rendered once. The column layout is what its `mt-4` assumed.
 */
export default function LoadingPage({
  className,
  children,
  ...rest
}: { className?: string } & React.HTMLAttributes<HTMLDivElement>) {
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);

  // Track loading page duration on unmount (when the real page replaces the skeleton).
  // Uses posthog.capture directly to avoid calling useAnalytics in a cleanup closure.
  useEffect(() => {
    const timer = startForegroundTimer();
    pathnameRef.current = pathname;
    return () => {
      timer.stop();
      const durationMs = clampDurationMs(timer.elapsedMs());
      if (durationMs !== null) {
        try {
          captureProductClientEvent(ANALYTICS_EVENTS.LOADING_PAGE_SHOWN, {
            route: pathnameRef.current,
            duration_ms: durationMs,
            slow: durationMs > 3000,
            was_hidden: timer.wasHidden(),
          });
        } catch {
          // capture must never break unmount cleanup — otherwise the
          // route-interactive ready signal below would also be skipped.
        }
      }
      // BRE-4346: signal route.interactive that the spinner is gone.
      // Fire even when the duration was dropped — the ready signal is
      // for the tracker, not the loading_page_shown event.
      markRouteInteractiveReady(pathnameRef.current, "loading_page", "unknown");
    };
    // Only on mount/unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={cn(
        // A loading frame stands in for a page, so it sits on the page surface;
        // a panel that renders one passes its own surface in className.
        "flex flex-col justify-center items-center h-screen w-full",
        PAGE_SURFACE_CLASS,
        className
      )}
      {...rest}
    >
      <svg
        width="64"
        height="64"
        viewBox="0 0 39 39"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="animate-pulse motion-reduce:animate-none"
        role="status"
        aria-label="Loading"
      >
        <path
          d="M26.1925 22.8313V14.69C26.1925 13.7538 25.4456 12.9947 24.5245 12.9947H15.925C15.0066 12.9947 14.2598 12.2356 14.2569 11.3036L14.2498 1.69458C14.2498 0.187901 12.4543 -0.566878 11.4055 0.499032L0.494599 11.5874C0.1814 11.9057 0.00708594 12.3335 0.00708594 12.78C0.00708594 15.0718 0 22.4121 0 25.777C0 26.7132 0.746859 27.468 1.66803 27.468H10.2491C11.1703 27.468 11.9171 28.2271 11.9171 29.1634V37.3046C11.9171 38.2409 12.664 39 13.5852 39L26.4008 38.9942C26.8416 38.9942 27.2653 38.8156 27.5771 38.4987L38.5079 27.4046C39.5594 26.3387 38.814 24.5108 37.3288 24.5137L27.8648 24.5238C26.9436 24.5238 26.1967 23.7647 26.1967 22.8284L26.1939 22.8313H26.1925Z"
          fill="currentColor"
          className="text-primary"
        />
      </svg>
      {children}
    </div>
  );
}
